import {
  all,
  put,
  take,
  fork,
  call,
  delay,
  cancel,
  select,
  debounce,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { errCode, errMessage } from '../../lib/apiError';
import { customerZone, shiftDateKey, currentNightKey } from '../../lib/dates';
import { selectCachedWeek, selectCustomerId, selectCachedNight } from '../selectors/coachInsights';
import {
  fetchNight,
  fetchNotes,
  createNote,
  fetchIntake,
  fetchTrends,
  fetchActivity,
  fetchOverview,
  fetchCaseload,
  saveCoachProfile,
  fetchCoachProfile,
  fetchDailyCheckIns,
  fetchInterventions,
  decideIntervention,
  fetchSupportHistory,
} from '../apis/coachInsights';
import {
  searchAction,
  searchFailure,
  searchSuccess,
  weekRequested,
  nightRequested,
  panelRequested,
  getWeekSuccess,
  getWeekFailure,
  trendsRequested,
  dashboardClosed,
  dashboardOpened,
  getNightFailure,
  getNightSuccess,
  getNotesFailure,
  getNotesSuccess,
  customerSelected,
  createNoteAction,
  getTrendsFailure,
  getTrendsSuccess,
  getIntakeFailure,
  getIntakeSuccess,
  activityRequested,
  createNoteFailure,
  createNoteSuccess,
  getCaseloadAction,
  getSupportFailure,
  getSupportSuccess,
  checkInsRequested,
  getProfileFailure,
  getProfileSuccess,
  saveProfileAction,
  getCheckInsFailure,
  getCheckInsSuccess,
  getCaseloadFailure,
  getCaseloadSuccess,
  getOverviewFailure,
  getOverviewSuccess,
  saveProfileFailure,
  saveProfileSuccess,
  getActivityFailure,
  getActivitySuccess,
  loadMoreNotesAction,
  getInterventionsFailure,
  getInterventionsSuccess,
  decideInterventionAction,
  decideInterventionFailure,
  decideInterventionSuccess,
} from '../slices/coachInsights';

// A cached night is served as-is for this long, then refreshed in the background.
const NIGHT_FRESH_MS = 5 * 60 * 1000;
const CASELOAD_PAGE_SIZE = 50;
const SEARCH_LIMIT = 8;

function* handleCaseload(action) {
  const { query = '', page = 1 } = action.payload || {};
  try {
    const params = { page, limit: CASELOAD_PAGE_SIZE };
    if (query) params.q = query;
    const response = yield call(fetchCaseload, params);
    yield put(getCaseloadSuccess({ items: response.data || [], total: response.total || 0, page }));
  } catch (error) {
    yield put(getCaseloadFailure(errMessage(error, 'Could not load customers')));
  }
}

function* handleSearch(action) {
  const query = action.payload;
  if (!query) return;
  try {
    const response = yield call(fetchCaseload, { q: query, limit: SEARCH_LIMIT });
    yield put(searchSuccess({ query, items: response.data || [] }));
  } catch (error) {
    yield put(searchFailure({ query, error: errMessage(error, 'Search failed') }));
  }
}

function* loadNight(customerId, date) {
  try {
    const response = yield call(fetchNight, customerId, date);
    yield put(getNightSuccess({ customerId, date, data: response.data, fetchedAt: Date.now() }));
    return true;
  } catch (error) {
    yield put(
      getNightFailure({ customerId, date, error: errMessage(error, 'Could not load this night') })
    );
    return false;
  }
}

function* handleNight(action) {
  const { date, force } = action.payload;
  const customerId = yield select(selectCustomerId);
  if (!customerId) return;
  const cached = yield select(selectCachedNight, customerId, date);
  // `force` is the coach's refresh: skip the cache however fresh it is.
  const fresh = !force && cached && Date.now() - cached.fetchedAt < NIGHT_FRESH_MS;
  if (!fresh) {
    const ok = yield call(loadNight, customerId, date);
    if (!ok) return;
  }
  // Warm the previous night after the current one is on screen.
  yield delay(1500);
  const previous = shiftDateKey(date, -1);
  const cachedPrevious = yield select(selectCachedNight, customerId, previous);
  if (!cachedPrevious) yield call(loadNight, customerId, previous);
}

function* handleActivity(action) {
  const { date } = action.payload;
  const customerId = yield select(selectCustomerId);
  try {
    const response = yield call(fetchActivity, customerId, date);
    yield put(getActivitySuccess({ customerId, date, data: response.data }));
  } catch (error) {
    yield put(
      getActivityFailure({ customerId, date, error: errMessage(error, 'Could not load activity') })
    );
  }
}

function* handleCustomer(action) {
  const { customerId, date } = action.payload;
  try {
    const response = yield call(fetchOverview, customerId);
    yield put(getOverviewSuccess({ customerId, data: response.data }));
    const night =
      date || response.data?.latest_night || currentNightKey(customerZone(response.data));
    yield put(nightRequested({ date: night }));
    yield put(activityRequested({ date: night }));
  } catch (error) {
    yield put(
      getOverviewFailure({ customerId, error: errMessage(error, 'Could not load this customer') })
    );
  }
}

function* handleTrends(action) {
  const { period, metrics, anchor } = action.payload;
  const customerId = yield select(selectCustomerId);
  const key = `${period}|${metrics.join(',')}|${anchor}`;
  try {
    const params = { period, metrics: metrics.join(',') };
    if (anchor) params.anchor = anchor;
    const response = yield call(fetchTrends, customerId, params);
    yield put(getTrendsSuccess({ customerId, key, data: response.data }));
  } catch (error) {
    yield put(
      getTrendsFailure({ customerId, key, error: errMessage(error, 'Could not load trends') })
    );
  }
}

function* loadProfile(customerId) {
  try {
    const response = yield call(fetchCoachProfile, customerId);
    yield put(getProfileSuccess({ customerId, data: response.data }));
  } catch (error) {
    yield put(
      getProfileFailure({ customerId, error: errMessage(error, 'Could not load the profile') })
    );
  }
}

const NOTES_PAGE_SIZE = 50;

function* loadNotes(customerId, page = 1) {
  try {
    const response = yield call(fetchNotes, customerId, { page, limit: NOTES_PAGE_SIZE });
    yield put(
      getNotesSuccess({ customerId, page, items: response.data || [], total: response.total })
    );
  } catch (error) {
    yield put(getNotesFailure({ customerId, error: errMessage(error, 'Could not load notes') }));
  }
}

function* loadLogs(customerId) {
  try {
    const response = yield call(fetchInterventions, customerId);
    yield put(getInterventionsSuccess({ customerId, items: response.data || [] }));
  } catch (error) {
    yield put(
      getInterventionsFailure({
        customerId,
        error: errMessage(error, 'Could not load the intervention log'),
      })
    );
  }
  try {
    const response = yield call(fetchSupportHistory, customerId);
    yield put(getSupportSuccess({ customerId, items: response.data || [] }));
  } catch (error) {
    yield put(
      getSupportFailure({ customerId, error: errMessage(error, 'Could not load support history') })
    );
  }
}

const CONSENT_REQUIRED = 'coach_consent_required';

// Every intake and check-in read is audited, so a repeat request while one is in flight
// shares it instead of starting another. Keys are per customer and resource.
const inFlight = new Set();

function* once(key, worker, ...args) {
  if (inFlight.has(key)) return;
  inFlight.add(key);
  try {
    yield call(worker, ...args);
  } finally {
    inFlight.delete(key);
  }
}

function* loadIntake(customerId) {
  try {
    const data = yield call(fetchIntake, customerId);
    yield put(getIntakeSuccess({ customerId, data }));
  } catch (error) {
    yield put(
      getIntakeFailure({
        customerId,
        error: errMessage(error, 'Could not load the intake'),
        locked: errCode(error) === CONSENT_REQUIRED,
      })
    );
  }
}

function* loadCheckInMonth(customerId, month) {
  try {
    const data = yield call(fetchDailyCheckIns, customerId, month);
    yield put(getCheckInsSuccess({ customerId, month, data }));
  } catch (error) {
    yield put(
      getCheckInsFailure({
        customerId,
        month,
        error: errMessage(error, 'Could not load the check-ins'),
        locked: errCode(error) === CONSENT_REQUIRED,
      })
    );
  }
}

function* handleCheckIns(action) {
  const customerId = yield select(selectCustomerId);
  if (!customerId) return;
  const checkIns = yield select((state) => state.coachInsights.customer.checkIns);
  if (checkIns.locked) return;
  const months = action.payload.months.filter((month) => !checkIns.months[month]);
  yield all(
    months.map((month) =>
      call(once, `${customerId}:check-ins:${month}`, loadCheckInMonth, customerId, month)
    )
  );
}

function* handlePanel(action) {
  const { tab, force } = action.payload;
  const customerId = yield select(selectCustomerId);
  if (!customerId) return;
  const customer = yield select((state) => state.coachInsights.customer);
  // A tab reopened while its read is in flight shares that read.
  if (tab === 'profile' && (force || !customer.profile.data)) {
    yield call(once, `${customerId}:profile`, loadProfile, customerId);
  }
  if (tab === 'notes' && (force || !customer.notes.loaded)) {
    yield call(once, `${customerId}:notes`, loadNotes, customerId);
  }
  if (tab === 'logs' && (force || !customer.interventions.loaded || !customer.support.loaded)) {
    yield call(once, `${customerId}:logs`, loadLogs, customerId);
  }
  if (tab === 'intake' && (force || !customer.intake.data) && !customer.intake.locked) {
    yield call(once, `${customerId}:intake`, loadIntake, customerId);
  }
}

function* handleLoadMoreNotes() {
  const customerId = yield select(selectCustomerId);
  const page = yield select((state) => state.coachInsights.customer.notes.page);
  yield call(loadNotes, customerId, page + 1);
}

function* handleSaveProfile(action) {
  const customerId = yield select(selectCustomerId);
  try {
    const response = yield call(saveCoachProfile, customerId, action.payload);
    yield put(saveProfileSuccess({ customerId, data: response.data }));
  } catch (error) {
    yield put(
      saveProfileFailure({ customerId, error: errMessage(error, 'Could not save the profile') })
    );
  }
}

function* handleCreateNote(action) {
  const customerId = yield select(selectCustomerId);
  try {
    const response = yield call(createNote, customerId, action.payload);
    yield put(createNoteSuccess({ customerId, note: response.data }));
  } catch (error) {
    yield put(
      createNoteFailure({ customerId, error: errMessage(error, 'Could not save the note') })
    );
  }
}

function* handleDecide(action) {
  const { interventionId, outcome } = action.payload;
  const customerId = yield select(selectCustomerId);
  try {
    const response = yield call(decideIntervention, customerId, interventionId, outcome);
    yield put(decideInterventionSuccess({ customerId, intervention: response.data }));
  } catch (error) {
    yield put(
      decideInterventionFailure({
        customerId,
        error: errMessage(error, 'Could not record the outcome'),
      })
    );
  }
}

// Both comparison cards, as the app draws them from the trend API: one read per night.
const WEEK_METRICS = ['surges', 'sr', 'ss'];

const stale = (entry) => !entry || Date.now() - entry.fetchedAt >= NIGHT_FRESH_MS;

function* handleWeek(action) {
  const { date, force } = action.payload;
  const customerId = yield select(selectCustomerId);
  if (!customerId || !date) return;
  const cached = yield select(selectCachedWeek, customerId, date);
  const night = yield select(selectCachedNight, customerId, date);
  // Re-read with the night (forced or past its freshness) and once the week itself is that old, so their scores agree.
  if (cached?.series && !force && !stale(night) && !stale(cached)) return;
  yield put(weekRequested({ customerId, date }));
  try {
    const params = { period: 'week', metrics: WEEK_METRICS.join(','), anchor: date };
    const response = yield call(fetchTrends, customerId, params);
    const series = response.data?.series ?? [];
    yield put(getWeekSuccess({ customerId, date, series, fetchedAt: Date.now() }));
  } catch (error) {
    yield put(
      getWeekFailure({ customerId, date, error: errMessage(error, 'Could not load the week') })
    );
  }
}

// Every watcher registers here so dashboardClosed cancels the whole tree, in-flight
// handlers included - a late response can never write into a closed dashboard.
function* dashboardWatchers() {
  yield takeLatest(getCaseloadAction.type, handleCaseload);
  yield debounce(300, searchAction.type, handleSearch);
  yield takeLatest(customerSelected.type, handleCustomer);
  yield takeLatest(nightRequested.type, handleNight);
  yield takeLatest(nightRequested.type, handleWeek);
  yield takeLatest(activityRequested.type, handleActivity);
  yield takeLatest(trendsRequested.type, handleTrends);
  yield takeEvery(panelRequested.type, handlePanel);
  yield takeEvery(checkInsRequested.type, handleCheckIns);
  yield takeLatest(loadMoreNotesAction.type, handleLoadMoreNotes);
  yield takeLatest(saveProfileAction.type, handleSaveProfile);
  yield takeEvery(createNoteAction.type, handleCreateNote);
  yield takeEvery(decideInterventionAction.type, handleDecide);
}

function* handleDashboardOpened() {
  const watchers = yield fork(dashboardWatchers);
  yield put(getCaseloadAction({ page: 1 }));
  // Child effects run before the layout's, so a selection may already be waiting.
  const pending = yield select((state) => state.coachInsights);
  if (pending.customerId && pending.customer.overview.loading && !pending.customer.overview.data) {
    yield fork(handleCustomer, {
      payload: { customerId: pending.customerId, date: pending.requestedDate },
    });
  }
  yield take(dashboardClosed.type);
  yield cancel(watchers);
}

export default function* coachInsightsSaga() {
  yield takeLatest(dashboardOpened.type, handleDashboardOpened);
}
